// src/App.js
import "./App.css";
import "./components/UI/SDKLoginStyles.css";

import React, { useState, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

// ✅ Updated imports - use local authentication system instead of SDK
import { AuthProvider, useAuthContext } from "./contexts/AuthContext";

import ErrorBoundary from "./components/ErrorBoundary";
import { setupAuthInterceptors } from "./components/API/interceptors";

import Teams from "./components/VIEWS/Teams";
import SocialPlus from "./components/VIEWS/SocialPlus";
import Website from "./components/VIEWS/Website";
import PPC from "./components/VIEWS/PPC";
import AccountTable from "./components/UI/AccountTable";
import SEO from "./components/VIEWS/SEO";
import SocialMedia from "./components/VIEWS/SocialMedia";
import Audit from "./components/INTERNAL_DASHBOARDS/Audit";
import Content from "./components/VIEWS/Content";
import DirectorsDashboard from "./components/INTERNAL_DASHBOARDS/DirectorsDashboard";
import CoordinatorsDashboard from "./components/INTERNAL_DASHBOARDS/CoordinatorsDashboard";
import FulfillmentDashboard from "./components/INTERNAL_DASHBOARDS/FulfillmentDashboard";
import VPsDashboard from "./components/INTERNAL_DASHBOARDS/VPsDashboard";
import ProfitabilityDashboard from "./components/INTERNAL_DASHBOARDS/ProfitabilityDashboard";
import WebsiteDashboard from "./components/INTERNAL_DASHBOARDS/WebsiteDashboard";
import SalesStats from "./components/SalesStats";
import Stats from "./components/INTERNAL_DASHBOARDS/SalesStats/Stats";
import StatsLastMonth from "./components/INTERNAL_DASHBOARDS/SalesStats/StatsLastMonth";
import StatsLastQuarter from "./components/INTERNAL_DASHBOARDS/SalesStats/StatsLastQuarter";
import StatsThisQuarter from "./components/INTERNAL_DASHBOARDS/SalesStats/StatsThisQuarter";
import StatsThisLastMonth from "./components/INTERNAL_DASHBOARDS/SalesStats/StatsThisLastMonth";
import CashFlowDashboard from "./components/INTERNAL_DASHBOARDS/CashFlowDashboard";
import SalesLifecycleDashboard from "./components/INTERNAL_DASHBOARDS/SalesLifecycleDashboard";
import Logout from "./components/Logout";
import IPCheck from "./components/IPCheck";
import MobileNavigation from "./components/UI/MobileNavigation";
import {
  TicketsView,
  TasksView,
  WebsiteTeamView,
} from "./components/Management";
import { getSidebarParam } from "./components/Utils";
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import DataTable from "./components/INTERNAL_DASHBOARDS/CoordinatorsDashboard/DataTable";
import DirectorDataTable from "./components/INTERNAL_DASHBOARDS/DirectorsDashboard/DataTable";

// ✅ Move isDevelopment outside component to prevent recreation on every render
const isDevelopment = process.env.NODE_ENV === "development";

// ✅ Debug flag to bypass AuthProvider temporarily - REMOVE IN PRODUCTION
const BYPASS_AUTH_FOR_DEBUGGING = isDevelopment && false; // Set to false to re-enable auth

// ✅ Control StrictMode - disabled in development for better usability
const enableStrictMode = process.env.NODE_ENV === "production" || false; // Disabled in dev for normal behavior

// ✅ Initialize auth interceptors once when the module loads
setupAuthInterceptors();

// ✅ Debug component to see AuthProvider state - inside AuthProvider
function AuthDebugger() {
  const authContext = useAuthContext();

  if (isDevelopment) {
    console.log("[AuthDebugger] AuthProvider state:", {
      ready: authContext?.ready,
      loading: authContext?.loading,
      authState: authContext?.authState,
      user: !!authContext?.user,
      error: authContext?.error,
    });
  }

  return null;
}

// ✅ Routes component that includes AuthDebugger (must be inside AuthProvider)
function AppRoutesWithDebug() {
  return (
    <>
      <AuthDebugger />
      <IPCheck />
      <MobileNavigation />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <SalesStats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report"
          element={
            <ProtectedRoute>
              <AccountTable />
            </ProtectedRoute>
          }
        />
        <Route
          path="/socialplus"
          element={
            <ProtectedRoute>
              <SocialPlus />
            </ProtectedRoute>
          }
        />
        <Route
          path="/website"
          element={
            <ProtectedRoute>
              <Website />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ppc"
          element={
            <ProtectedRoute>
              <PPC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/seo"
          element={
            <ProtectedRoute>
              <SEO />
            </ProtectedRoute>
          }
        />
        <Route
          path="/socialmedia"
          element={
            <ProtectedRoute>
              <SocialMedia />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <Audit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content"
          element={
            <ProtectedRoute>
              <Content />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesstats"
          element={
            <ProtectedRoute>
              <SalesStats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logout"
          element={
            <ProtectedRoute>
              <Logout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directorsDashboard/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <DirectorsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directorsDashboard/data/:type/:color"
          element={
            <ProtectedRoute>
              <DirectorDataTable />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vpsDashboard/:vpId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <VPsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coordinatorsDashboard"
          element={
            <ProtectedRoute>
              <CoordinatorsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coordinatorsDashboard/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <CoordinatorsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coordinatorsDashboard/data/:type/:color"
          element={
            <ProtectedRoute>
              <DataTable />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillmentDashboard/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <FulfillmentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesFlowDashboard"
          element={
            <ProtectedRoute>
              <ProfitabilityDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesLifecycleDashboard"
          element={
            <ProtectedRoute>
              <SalesLifecycleDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashFlow"
          element={
            <ProtectedRoute>
              <CashFlowDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/WebsiteDashboard"
          element={
            <ProtectedRoute>
              <WebsiteDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesThisMonth/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <Stats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesThisLastMonth/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsThisLastMonth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesLastMonth/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsLastMonth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesLastQuarter/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsLastQuarter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesThisQuarter/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsThisQuarter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <TicketsView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <TasksView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/website-team"
          element={
            <ProtectedRoute>
              <WebsiteTeamView />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

// ✅ Routes without debug (for bypass mode)
function AppRoutesWithoutAuth() {
  return (
    <>
      <IPCheckWithoutAuth />
      <MobileNavigationWithoutAuth />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <SalesStats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report"
          element={
            <ProtectedRoute>
              <AccountTable />
            </ProtectedRoute>
          }
        />
        <Route
          path="/socialplus"
          element={
            <ProtectedRoute>
              <SocialPlus />
            </ProtectedRoute>
          }
        />
        <Route
          path="/website"
          element={
            <ProtectedRoute>
              <Website />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ppc"
          element={
            <ProtectedRoute>
              <PPC />
            </ProtectedRoute>
          }
        />
        <Route
          path="/seo"
          element={
            <ProtectedRoute>
              <SEO />
            </ProtectedRoute>
          }
        />
        <Route
          path="/socialmedia"
          element={
            <ProtectedRoute>
              <SocialMedia />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <Audit />
            </ProtectedRoute>
          }
        />
        <Route
          path="/content"
          element={
            <ProtectedRoute>
              <Content />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesstats"
          element={
            <ProtectedRoute>
              <SalesStats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/logout"
          element={
            <ProtectedRoute>
              <Logout />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directorsDashboard/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <DirectorsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/directorsDashboard/data/:type/:color"
          element={
            <ProtectedRoute>
              <DirectorDataTable />
            </ProtectedRoute>
          }
        />
        <Route
          path="/vpsDashboard/:vpId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <VPsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coordinatorsDashboard"
          element={
            <ProtectedRoute>
              <CoordinatorsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coordinatorsDashboard/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <CoordinatorsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coordinatorsDashboard/data/:type/:color"
          element={
            <ProtectedRoute>
              <DataTable />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillmentDashboard/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <FulfillmentDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesFlowDashboard"
          element={
            <ProtectedRoute>
              <ProfitabilityDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesLifecycleDashboard"
          element={
            <ProtectedRoute>
              <SalesLifecycleDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cashFlow"
          element={
            <ProtectedRoute>
              <CashFlowDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/WebsiteDashboard"
          element={
            <ProtectedRoute>
              <WebsiteDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesThisMonth/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <Stats />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesThisLastMonth/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsThisLastMonth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesLastMonth/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsLastMonth />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesLastQuarter/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsLastQuarter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/salesThisQuarter/:owId?/:sidebar?/:hidediv?"
          element={
            <ProtectedRoute>
              <StatsThisQuarter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tickets"
          element={
            <ProtectedRoute>
              <TicketsView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks"
          element={
            <ProtectedRoute>
              <TasksView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/website-team"
          element={
            <ProtectedRoute>
              <WebsiteTeamView />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

// ✅ IPCheck component that works without AuthProvider (for bypass mode)
function IPCheckWithoutAuth() {
  // Simple IP check without auth context
  const [ipChecked, setIpChecked] = useState(false);

  React.useEffect(() => {
    if (ipChecked) return;

    fetch("https://api64.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => {
        if (isDevelopment) {
          console.log("[IPCheckWithoutAuth] Current IP:", data.ip);
        }
        setIpChecked(true);
      })
      .catch((error) => {
        if (isDevelopment) {
          console.error("[IPCheckWithoutAuth] IP check failed:", error);
        }
        setIpChecked(true);
      });
  }, [ipChecked]);

  return null;
}

// ✅ Simple MobileNavigation for bypass mode (no auth context)
function MobileNavigationWithoutAuth() {
  // In bypass mode, we don't show mobile navigation since we don't have user info
  return null;
}

// ✅ Mock AuthProvider for bypass mode - provides expected context structure
function MockAuthProvider({ children }) {
  // Create a React context for mock auth (since we can't import AuthContext from SDK)
  const MockAuthContext = React.createContext();

  const mockContextValue = {
    ready: true,
    loading: false,
    authState: "authenticated",
    user: {
      // Updated from userInfo to user to match our local auth system
      id: "mock-user-id",
      name: "Debug User",
      email: "debug@example.com",
      role: "admin", // Updated from userRole to role
    },
    error: null, // Updated from authError to error
    isAuthenticated: true,
    login: () => console.log("[MockAuth] Login called"),
    loginWithMethod: () => console.log("[MockAuth] LoginWithMethod called"),
    logout: () => console.log("[MockAuth] Logout called"),
    hasRole: (role) => role === "admin", // Mock role checker
    getToken: () => "mock-token",
    clearError: () => console.log("[MockAuth] ClearError called"),
    refreshUser: () => console.log("[MockAuth] RefreshUser called"),
    capabilities: {
      recommendedMethod: "popup",
      isInIframe: false,
      isMobile: false,
    },
    sidebar: getSidebarParam(),
  };

  return React.createElement(
    MockAuthContext.Provider,
    { value: mockContextValue },
    children
  );
}

function App() {
  const [appReady, setAppReady] = useState(false);

  // ✅ Memoize sidebar value to prevent re-renders
  const sidebar = useMemo(() => getSidebarParam(), []);

  // SDK configuration is handled in src/index.js - no need for duplicate config here

  if (isDevelopment) {
    console.log("[App] Component rendering, appReady:", appReady);
  }

  // Set up auto-refresh for sidebar mode
  React.useEffect(() => {
    if (sidebar) {
      const refreshInterval = setTimeout(() => {
        window.location.reload();
      }, 600000); // 10 minutes

      return () => clearTimeout(refreshInterval);
    }
  }, [sidebar]);

  // App is ready after initial render
  React.useEffect(() => {
    setAppReady(true);
  }, []);

  /////////////////////////////////////////////// USE THIS FLAG TO PUT EVERYTHING IN MAINTENANCE
  const inMaintenance = false;

  if (!appReady) {
    return null; // Don't render anything until app is ready
  }

  if (isDevelopment) {
    console.log("[App] App ready, rendering main content");
    console.log(
      "[App] About to render AuthProvider - this will take over rendering"
    );
  }

  const AppContent = () => (
    <ErrorBoundary>
      {BYPASS_AUTH_FOR_DEBUGGING ? (
        <MockAuthProvider>
          <AppRoutesWithoutAuth />
        </MockAuthProvider>
      ) : (
        <AuthProvider>
          <AppRoutesWithDebug />
        </AuthProvider>
      )}
    </ErrorBoundary>
  );

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
